import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, TargetIcon, TrendingUpIcon, CpuIcon, GraduationCapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Container, StatBlock, Reveal } from '../components/ui/Primitives';
import { HeroArcs, PlatformMock } from '../components/sections/HeroVisual';
import { ExplainerVideo } from '../components/sections/ExplainerVideo';
import { PressStrip } from '../components/sections/PressStrip';
import { PhotoStrip } from '../components/sections/PhotoStrip';
import { PHOTOS } from '../data/photos';
import { Faqs } from '../components/sections/Faqs';
import { GENERAL_FAQS } from '../data/faqs';
import { nextWebinar, SESSION_TIME } from '../data/webinars';
import { sessionDateLabel } from '../components/webinars/SessionTime';
import { SERVICE_LINES, METHOD_STEPS, SECTORS } from '../data/services';
import { ARTICLES } from '../data/insights';
import { RESOURCES } from '../data/resources';
const CAPABILITY_CARDS = [{
  icon: TargetIcon,
  title: 'Market decisions',
  detail: 'Size a market from the bottom up, name the competitors and their terms, and cost the entry route.',
  to: '/services/intelligence'
}, {
  icon: TrendingUpIcon,
  title: 'Demand and revenue',
  detail: 'Choose the segments, price the offer and build the pipeline that makes demand arrive predictably.',
  to: '/services/growth'
}, {
  icon: CpuIcon,
  title: 'AI adoption',
  detail: 'Put AI where it moves a number your board already watches, with the governance written first.',
  to: '/services/ai'
}, {
  icon: GraduationCapIcon,
  title: 'Capability building',
  detail: 'Hand over the method, the models and the training so the work continues without us.',
  to: '/augmented-intelligence'
}];
export function Home() {
  const reduced = useReducedMotion();
  const featured = ARTICLES.slice(0, 3);
  const playbook = RESOURCES[0];
  const nextSession = nextWebinar();
  return <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden surface-dark" data-id="element-2539" data-mp="1om2ia0i6jru2">
        <HeroArcs className="pointer-events-none absolute -right-10 top-0 h-[420px] w-[420px] opacity-90" data-id="element-2540" data-mp="1ojzgkj7o0a9i" />
        <Container className="relative py-16 lg:py-24" data-id="element-2541" data-mp="1flmeepxiyioo">
          <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.05fr]" data-id="element-2542" data-mp="1fmfghdljgk0h">
            <motion.div initial={reduced ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-2543" data-mp="23c9onhuwxkee">
              <h1 className="font-display text-[40px] font-light leading-[1.06] text-white sm:text-[56px] lg:text-[64px]" data-id="element-2544" data-mp="1tkm31xl1exku">
                Empowering the organisations shaping African markets
              </h1>
              <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-2545" data-mp="2m8hykyyvejg1">
                Market intelligence, data led growth and augmented intelligence, delivered as evidence you can
                take into the room where the decision is made.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-2546" data-mp="alxy94zz0f2s">
                <Button to="/contact" size="lg" shape="pill" data-id="element-2547" data-mp="oennrjepk24n">
                  Book a discovery call
                </Button>
                <Button to="/assessment" variant="reversed" size="lg" shape="pill" data-id="element-2548" data-mp="kdtvyey5a36h">
                  Take the AI readiness assessment
                </Button>
              </div>
            </motion.div>

            <motion.div className="flex justify-center lg:justify-end" initial={reduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.12
          }} data-id="element-2549" data-mp="3nutyz1d6vnnd">
              <PlatformMock data-id="element-2550" data-mp="1h1rwnzgx90lp" />
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Press */}
      <PressStrip data-id="element-2551" data-mp="1c3gk3gz8sbzs" />

      <PhotoStrip id="field" title="The work happens in rooms like these" intro="Workshops with ministry teams, meetings with the organisations we learn from, and time in the places where the data comes from." photos={[PHOTOS.workshopWide, PHOTOS.govTraining, PHOTOS.kigaliMeeting]} data-id="element-2552" data-mp="1c62q3c6hijfw" />

      {/* Capability cards */}
      <section aria-labelledby="capabilities" className="bg-charcoal" data-id="element-2553" data-mp="2mc76b51ottx6">
        <Container className="py-16 lg:py-24" data-id="element-2554" data-mp="2qdai9ggm75t4">
          <h2 id="capabilities" className="max-w-4xl font-display text-[30px] font-light leading-tight text-white sm:text-[42px]" data-id="element-2555" data-mp="2qch805ig5y68">
            Intelligence, method and technology for:
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2556" data-mp="351nfegrfuup7">
            {CAPABILITY_CARDS.map((card, index) => {
            const Icon = card.icon;
            return <Reveal key={card.title} delay={index * 0.06} className="h-full" data-id="element-2557" data-mp="3e2bk2kuxzipf" data-mpi={index}>
                  <Link to={card.to} className="group flex h-full flex-col border border-white/20 p-7 transition-colors duration-200 hover:border-gold" data-id="element-2558" data-mp="3e08xybwwu9pz">
                    <Icon className="h-8 w-8 text-white" aria-hidden="true" strokeWidth={1.4} data-id="element-2559" data-mp="yfr452x7ix3j" />
                    <h3 className="mt-6 font-display text-[24px] leading-snug text-white" data-id="element-2560" data-mp="tg4suz0xvqxd">{card.title}</h3>
                    <p className="mt-4 text-[15px] leading-relaxed text-white/70" data-id="element-2561" data-mp="1m2zazpaaae7o">{card.detail}</p>
                    <span className="mt-auto inline-flex items-center gap-2 pt-7 text-[14px] font-medium text-gold" data-id="element-2562" data-mp="1h3w562mhc3yn">
                      Learn more
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-2563" data-mp="1cbuweqoisvdd" />
                    </span>
                  </Link>
                </Reveal>;
          })}
          </div>
        </Container>
      </section>

      {/* Animated explainer */}
      <section aria-labelledby="explainer" className="bg-charcoal" data-id="element-2564" data-mp="3ubvqibwf803j">
        <Container className="py-16 lg:py-24" data-id="element-2565" data-mp="27bde33qcy3z">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-2566" data-mp="269y1mfolpgg">
            <div className="max-w-2xl" data-id="element-2567" data-mp="3kjq2fd9ai2tm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-2568" data-mp="3kiol4r1okk3g">Watch</p>
              <h2 id="explainer" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-2569" data-mp="3phj53wbdwep5">
                How Cavalla Solution works
              </h2>
              <p className="mt-5 text-[17px] text-body" data-id="element-2570" data-mp="3fn99m5r5b0a7">
                Half a minute on how an engagement runs, from scattered evidence to a decision somebody owns.
              </p>
            </div>
          </div>
          <div className="mt-10" data-id="element-2571" data-mp="36n46w8wwcq2d">
            <ExplainerVideo data-id="element-2572" data-mp="zu0n6z4ocf0e" />
          </div>
        </Container>
      </section>

      {/* Method */}
      <section aria-labelledby="method" className="bg-charcoal" data-id="element-2573" data-mp="14u6h6lyf2dln">
        <Container className="py-16 lg:py-24" data-id="element-2574" data-mp="14rakt227vwzb">
          <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-2575" data-mp="1eymxfgft5wpz">
            <div className="lg:sticky lg:top-32 lg:self-start" data-id="element-2576" data-mp="19yqs9dvdjz8u">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-2577" data-mp="1a0bmf2ds1g66">Our method</p>
              <h2 id="method" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-2578" data-mp="1nrzzp8zriaoq">
                Four steps, run on every engagement
              </h2>
              <p className="mt-5 max-w-md text-[17px] text-body" data-id="element-2579" data-mp="1xmi7ap8auqdt">
                Augmented intelligence is the deliberate pairing of machine analysis with human judgement,
                arranged so a person stays accountable for the decision.
              </p>
              <Button to="/augmented-intelligence" variant="secondary" shape="pill" className="mt-8" data-id="element-2580" data-mp="22mwd00mfho8o">
                Walk through the method
              </Button>
            </div>

            <ol className="divide-y divide-line border-t border-line" data-id="element-2581" data-mp="89eni82gqhb0">
              {METHOD_STEPS.map((step, index) => <li key={step.id} className="grid gap-4 py-8 sm:grid-cols-[64px_1fr]" data-id="element-2582" data-mp="wt0eqmms8rsh" data-mpi={index}>
                  <span className="font-display text-[26px] leading-none text-gold" data-id="element-2583" data-mp="11sd012y09qsy">0{index + 1}</span>
                  <div data-id="element-2584" data-mp="16jmnpxzzuk27">
                    <h3 className="font-display text-[24px] leading-snug text-ink" data-id="element-2585" data-mp="16kekhc5bunu2">{step.title}</h3>
                    <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-2586" data-mp="1akyggce5zlem">{step.summary}</p>
                    <p className="mt-4 text-[13px] uppercase tracking-[0.12em] text-muted" data-id="element-2587" data-mp="dwine6buitwr">
                      You receive: <span className="normal-case tracking-normal text-body" data-id="element-2588" data-mp="iutyekdvnagk">{step.output}</span>
                    </p>
                  </div>
                </li>)}
            </ol>
          </div>
        </Container>
      </section>

      {/* Statistics */}
      <section aria-labelledby="numbers" className="bg-mist" data-id="element-2589" data-mp="1ybu1mqo47icw">
        <Container className="py-16 lg:py-20" data-id="element-2590" data-mp="1ydf18upadrhm">
          <h2 id="numbers" className="font-display text-[28px] leading-tight text-ink sm:text-[36px]" data-id="element-2591" data-mp="2393ygrdu2war">
            The market we work in, in figures
          </h2>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2592" data-mp="28f96tgohqezi">
            <Reveal data-id="element-2593" data-mp="28hbtprucjs00"><StatBlock value={16.5} decimals={1} prefix="$" suffix="bn" label="Africa AI market by 2030" source="Market forecast, 2025" data-id="element-2594" data-mp="28hbwu0pn8848" /></Reveal>
            <Reveal delay={0.05} data-id="element-2595" data-mp="1zlmgi631y3xp"><StatBlock value={2.9} decimals={1} suffix="x" label="Growth in enterprise AI spend since 2023" source="Cavalla analysis, 2026" data-id="element-2596" data-mp="1zm5tr9hrc45e" /></Reveal>
            <Reveal delay={0.1} data-id="element-2597" data-mp="1pnyi9668meas"><StatBlock value={61} suffix="%" label="Of programmes stall on data foundations, not models" source="Cavalla client review, 2026" data-id="element-2598" data-mp="1pngd1k1lkpqq" /></Reveal>
            <Reveal delay={0.15} data-id="element-2599" data-mp="1kr81shqitgou"><StatBlock value={10} label="Priority sectors under continuous coverage" source="Cavalla sector coverage" data-id="element-2600" data-mp="1go32sp5nfx4y" /></Reveal>
          </div>
        </Container>
      </section>

      {/* Service lines */}
      <section aria-labelledby="lines" className="bg-charcoal" data-id="element-2601" data-mp="3b1b2au4kkwwx">
        <Container className="py-16 lg:py-24" data-id="element-2602" data-mp="3azq9pa1tfo1p">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-2603" data-mp="cntkjxaj4m1o">
            <div className="max-w-2xl" data-id="element-2604" data-mp="8n9pcz9insi6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-2605" data-mp="8m83dzqypllo">Three lines</p>
              <h2 id="lines" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-2606" data-mp="mm95tjmvl139">
                Start with the question you actually have
              </h2>
            </div>
            <Button to="/services" variant="secondary" shape="pill" size="sm" data-id="element-2607" data-mp="3q3raxjb9jtzz">
              How we work
            </Button>
          </div>

          <div className="mt-12 grid gap-px bg-line lg:grid-cols-3" data-id="element-2608" data-mp="3nn360bpn1x8">
            {SERVICE_LINES.map(line => <article key={line.id} className="flex flex-col bg-white p-8" data-id="element-2609" data-mp="3v2ay1n1lfbtx">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-2610" data-mp="37d01yiv6u5m4">{line.short}</p>
                <h3 className="mt-4 font-display text-[27px] leading-tight text-ink" data-id="element-2611" data-mp="32ef3anvl3tzs">{line.name}</h3>
                <p className="mt-4 text-[16px] text-body" data-id="element-2612" data-mp="1hvcndh4ra068">{line.promise}</p>
                <ul className="mt-6 space-y-2.5 text-[14px] text-muted" data-id="element-2613" data-mp="1mmkychlqhtli">
                  {line.subServices.map(sub => <li key={sub.name} className="flex gap-2.5" data-id="element-2614" data-mp="18vpvpn1tw8qd">
                      <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-2615" data-mp="18td7is5h924v" />
                      {sub.name}
                    </li>)}
                </ul>
                <div className="mt-auto pt-8" data-id="element-2616" data-mp="20kj8fat0v3v7">
                  <Link to={`/services/${line.id}`} className="inline-flex items-center gap-2 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-2617" data-mp="25ke2toc0abl0">
                    Explore this line
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2618" data-mp="g6sgxtwtsu2d" />
                  </Link>
                </div>
              </article>)}
          </div>

          {/* Solution teaser, distinct from the three advisory lines */}
          <Reveal className="mt-8" data-id="element-2619" data-mp="2mu9mcw9gm8ld">
            <Link to="/cavalla-gov" className="group flex flex-col gap-6 rounded-md border border-line bg-panel p-8 transition-colors duration-200 hover:border-gold sm:flex-row sm:items-center sm:justify-between" data-id="element-2620" data-mp="2hvok6randeua">
              <div className="max-w-2xl" data-id="element-2621" data-mp="292lb0v71bo5g">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-2622" data-mp="244a0ei8x19m6">
                  Our platform for government
                </p>
                <h3 className="mt-4 font-display text-[27px] leading-tight text-ink" data-id="element-2623" data-mp="1z3m0t86mfkgc">Cavalla Gov</h3>
                <p className="mt-3 text-[16px] text-body" data-id="element-2624" data-mp="1u6m2xkjxdo0b">
                  One live picture of how a national development plan is being delivered, and where development
                  partner funding lands against it.
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-2 text-[15px] font-medium text-royal" data-id="element-2625" data-mp="3ki03pms7ntur">
                See the platform
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-2626" data-mp="16aihu1uizo1l" />
              </span>
            </Link>
          </Reveal>
        </Container>
      </section>

      {/* Sectors */}
      <section aria-labelledby="sectors" className="surface-dark" data-id="element-2627" data-mp="1k1du7o48gn9x">
        <Container className="py-16 lg:py-20" data-id="element-2628" data-mp="ncy3hope07v8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]" data-id="element-2629" data-mp="ndzclnmuekab">
            <div data-id="element-2630" data-mp="sbsn2sv6i0ge">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-2631" data-mp="sb9a7qkdy298">Coverage</p>
              <h2 id="sectors" className="mt-4 font-display text-[30px] leading-tight text-white sm:text-[40px]" data-id="element-2632" data-mp="wep9kunuagpm">
                Ten priority sectors, tracked continuously
              </h2>
              <p className="mt-5 max-w-md text-[16px] text-white/70" data-id="element-2633" data-mp="27rhyzyt5qt2i">
                We hold a standing view of demand, policy and buyer behaviour in each of these, refreshed monthly
                rather than assembled when a client asks.
              </p>
            </div>
            <ul className="grid grid-cols-2 gap-px self-start bg-white/15 sm:grid-cols-3" data-id="element-2634" data-mp="26bvyo5zeuzji">
              {SECTORS.map(sector => <li key={sector} className="bg-panel px-5 py-6 text-[16px] text-white/85" data-id="element-2635" data-mp="2k80sqta74d8j">
                  {sector}
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      {/* Insights */}
      <section aria-labelledby="insights" className="bg-charcoal" data-id="element-2636" data-mp="2pa8a5ydsoli9">
        <Container className="py-16 lg:py-24" data-id="element-2637" data-mp="33ycu15ol332m">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-2638" data-mp="33x1hohh2omzw">
            <div data-id="element-2639" data-mp="2yyzxft7wuhmu">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-2640" data-mp="2z0ksdjy502l8">Insights</p>
              <h2 id="insights" className="mt-4 font-display text-[32px] leading-tight text-ink sm:text-[44px]" data-id="element-2641" data-mp="3czsw8q58vyvr">
                Analysis for people who have to act on it
              </h2>
            </div>
            <Button to="/insights" variant="secondary" shape="pill" size="sm" data-id="element-2642" data-mp="3rm9dsloiogk3">
              All insights
            </Button>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-3" data-id="element-2643" data-mp="3il1qszrgb25b">
            {featured.map((article, index) => <Reveal key={article.slug} delay={index * 0.06} className="h-full" data-id="element-2644" data-mp="3mm3uptwbc2dm" data-mpi={index}>
                <article className="group flex h-full flex-col" data-id="element-2645" data-mp="38v8kad661d7x">
                  <Link to={`/insights/${article.slug}`} className="block overflow-hidden" data-id="element-2646" data-mp="38txe66p8ztdn">
                    <img src={article.heroImage} alt={article.heroAlt} loading="lazy" className="aspect-[3/2] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" data-id="element-2647" data-mp="3dnjh375m0iw9" />
                  </Link>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold" data-id="element-2648" data-mp="2pyqhn0ps3sww">
                    {article.sectors[0]} · {article.readTime} minute read
                  </p>
                  <h3 className="mt-3 font-display text-[22px] leading-snug text-ink" data-id="element-2649" data-mp="10h3ecv4sr9wb">
                    <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-2650" data-mp="vmfmqnehxzdi">
                      {article.title}
                    </Link>
                  </h3>
                  <p className="mt-3 text-[15px] text-body" data-id="element-2651" data-mp="mposmgji8ulk">{article.excerpt}</p>
                </article>
              </Reveal>)}
          </div>
        </Container>
      </section>

      {/* eBook */}
      <section aria-labelledby="ebook" className="bg-champagne" data-id="element-2652" data-mp="1ecxfut38qwze">
        <Container className="py-16 lg:py-20" data-id="element-2653" data-mp="1ac3nnnirmw0p">
          <div className="grid items-center gap-12 lg:grid-cols-[0.7fr_1.3fr]" data-id="element-2654" data-mp="15ebwivpy3dxk">
            <img src={playbook.cover} alt={playbook.coverAlt} loading="lazy" className="mx-auto w-full max-w-[300px]" data-id="element-2655" data-mp="15gfyv98744vq" />
            <div data-id="element-2656" data-mp="3q39g5z0yr40i">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-2657" data-mp="3q6x84tqrfid4">Free eBook</p>
              <h2 id="ebook" className="mt-4 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-2658" data-mp="2almm2mk4zkqe">
                {playbook.title}
              </h2>
              <p className="mt-5 max-w-xl text-[17px] text-body" data-id="element-2659" data-mp="2elndr8w8xsfe">{playbook.summary}</p>
              <ul className="mt-6 space-y-2.5 text-[15px] text-body" data-id="element-2660" data-mp="2jeq65dpaevu0">
                {playbook.whatIsInside.slice(0, 3).map(item => <li key={item} className="flex gap-3" data-id="element-2661" data-mp="2ofdutsq1n0l2">
                    <span aria-hidden="true" className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-2662" data-mp="2tat6qf4hqmeu" />
                    {item}
                  </li>)}
              </ul>
              <Button to={`/resources/${playbook.slug}`} size="lg" shape="pill" className="mt-8" data-id="element-2663" data-mp="32brg7cxp7m4t">
                Download the playbook
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Client proof */}
      <section aria-labelledby="proof" className="bg-charcoal" data-id="element-2664" data-mp="180kya9lye1te">
        <Container className="py-16 lg:py-20" data-id="element-2665" data-mp="p0yekmsrkcpp">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-2666" data-mp="k3oduazxcemk">
            <div className="max-w-2xl" data-id="element-2667" data-mp="k1lrc0xzd3ml">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-2668" data-mp="xv2xmk1nojuo">Client words</p>
              <h2 id="proof" className="mt-4 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-2669" data-mp="tuanlokymaxc">
                We publish results, not promises
              </h2>
              <p className="mt-5 text-[16px] text-body" data-id="element-2670" data-mp="153rew8z9xf9">
                Case studies from our current engagements will appear here as each one completes, with the client
                named and the numbers agreed.
              </p>
            </div>
          </div>
          <div className="mt-10 grid gap-px bg-line sm:grid-cols-3" data-id="element-2671" data-mp="a7cy1b14l6nd">
            {['Banking, West Africa', 'Government, East Africa', 'NGO, multi country'].map(label => <div key={label} className="bg-mist p-8" data-id="element-2672" data-mp="1ljvvwn5r33sg">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-2673" data-mp="1u0wjxt3jgjg8">{label}</p>
                <p className="mt-5 font-display text-[20px] leading-snug text-muted/75" data-id="element-2674" data-mp="1y39pohiachd0">
                  Case study in preparation
                </p>
                <p className="mt-3 text-[14px] text-muted" data-id="element-2675" data-mp="1k5ctvnbnoeze">Published on completion.</p>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Next webinar */}
      {nextSession && <section aria-labelledby="next-webinar" className="border-y border-line bg-champagne" data-id="element-2676" data-mp="2hqqx6brrsa10">
          <Container className="py-11" data-id="element-2677" data-mp="23246jdwozvuh">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-2678" data-mp="2312sd0kdqt8j">
              <div className="max-w-2xl" data-id="element-2679" data-mp="2805sc6li699n">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-2680" data-mp="27yul1wsumjds">
                  Next webinar, free to attend
                </p>
                <h2 id="next-webinar" className="mt-3 font-display text-[23px] leading-snug text-ink sm:text-[27px]" data-id="element-2681" data-mp="30i9y8d28he0e">
                  {nextSession.title}
                </h2>
                <p className="mt-3 text-[15px] text-body" data-id="element-2682" data-mp="zz1vsdjexkhe">
                  {sessionDateLabel(nextSession)}, {SESSION_TIME.gmt}. For {nextSession.audience.toLowerCase()}.
                </p>
              </div>
              <Button to={`/webinars/${nextSession.slug}`} size="lg" shape="pill" className="shrink-0" data-id="element-2683" data-mp="r5ghswf2o791">
                Reserve a place
              </Button>
            </div>
          </Container>
        </section>}

      {/* Questions */}
      <Faqs id="home-faqs" kicker="Common questions" title="What people ask us first." items={GENERAL_FAQS} tone="mist" data-id="element-2684" data-mp="h5xymi6zkpi1" />

      {/* Closing call to action */}
      <section className="relative isolate overflow-hidden surface-dark" data-id="element-2685" data-mp="1xmkdweyyryy2">
        <HeroArcs className="pointer-events-none absolute -right-16 -top-8 h-[300px] w-[300px] opacity-70" data-id="element-2686" data-mp="1xnbvumzn1y5v" />
        <Container className="relative py-16 lg:py-20" data-id="element-2687" data-mp="22jki0kc96rmj">
          <div className="flex flex-col items-start justify-between gap-9 lg:flex-row lg:items-center" data-id="element-2688" data-mp="2cqn49a0w0i6z">
            <div className="max-w-xl" data-id="element-2689" data-mp="27rscvjoutlb9">
              <h2 className="font-display text-[30px] leading-tight text-white sm:text-[40px]" data-id="element-2690" data-mp="27oo0hz18jj9i">
                Tell us the decision you are facing
              </h2>
              <p className="mt-5 text-[17px] text-white/72" data-id="element-2691" data-mp="2gqdu9bilauag">
                A discovery call lasts thirty minutes. You leave it with a view on whether we are the right firm
                for the problem, and a first read on how we would approach it.
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row" data-id="element-2692" data-mp="34fq7cmh1fmi1">
              <Button to="/contact" size="lg" shape="pill" data-id="element-2693" data-mp="30emveb242am3">
                Book a discovery call
              </Button>
              <Button to="/services" variant="reversed" size="lg" shape="pill" data-id="element-2694" data-mp="1a3s0usa454pu">
                Explore our services
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>;
}